/**
 * 設計 Token 系統
 * 這個文件定義了應用程式的設計語言，包括顏色、排版、間距等基本元素
 */

const tokens = {
  // 顏色系統
  colors: {
    // 主色調 (紅色系)
    primary: {
      50: "#FFF5F5",
      100: "#FFE5E5",
      200: "#FFB8B8",
      300: "#FF8A8A",
      400: "#FF5C5C",
      500: "#FF2E2E", // 主紅色
      600: "#E60000",
      700: "#B80000",
      800: "#8A0000",
      900: "#5C0000",
    },
    // 輔助色調 (暖色系搭配)
    secondary: {
      100: "#FFF0E5",
      200: "#FFD1B8",
      300: "#FFB38A",
      400: "#FF945C",
      500: "#FF752E", // 橘紅色
      600: "#E65C00",
      700: "#B84700",
      800: "#8A3200",
      900: "#5C1D00",
    },
    // 輔助色 - 柔和的薄荷綠
    tertiary: {
      50: "#ECFDF5",
      100: "#D1FAE5",
      200: "#A7F3D0",
      300: "#6EE7B7",
      400: "#34D399",
      500: "#10B981",
      600: "#059669",
      700: "#047857",
      800: "#065F46",
      900: "#064E3B",
    },
    // 輔助色 - 柔和的薰衣草紫
    quaternary: {
      50: "#F5F3FF",
      100: "#EDE9FE",
      200: "#DDD6FE",
      300: "#C4B5FD",
      400: "#A78BFA",
      500: "#8B5CF6",
      600: "#7C3AED",
      700: "#6D28D9",
      800: "#5B21B6",
      900: "#4C1D95",
    },

    // 背景顏色 - 更柔和的背景色
    background: {
      paper: "#FFFFFF",
      default: "#F9FAFB",
      subtle: "#F3F4F6", // 新增柔和背景色
      dark: {
        paper: "#1F2937",
        default: "#111827",
        subtle: "#374151", // 新增暗色模式柔和背景色
      },
    },

    // 文字顏色 - 調整為更易讀的顏色
    text: {
      primary: "#1F2937", // 稍微淺一點，增加易讀性
      secondary: "#4B5563",
      tertiary: "#6B7280", // 新增第三級文字顏色
      disabled: "#9CA3AF",
      dark: {
        primary: "#F9FAFB",
        secondary: "#E5E7EB", // 調亮，增加易讀性
        tertiary: "#D1D5DB", // 新增第三級文字顏色
        disabled: "#6B7280",
      },
    },

    // 邊框顏色 - 更柔和的邊框色
    border: {
      light: "#E5E7EB",
      main: "#D1D5DB",
      dark: "#374151",
      focus: "#FF2E2E", // 新增焦點邊框色
    },
    // 灰階 - 保持不變
    gray: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
    // 功能性顏色 - 調整為更柔和的色調
    success: {
      light: "#D1FAE5",
      main: "#10B981",
      dark: "#059669",
    },
    warning: {
      light: "#FEF3C7",
      main: "#F59E0B",
      dark: "#D97706",
    },
    error: {
      light: "#FEE2E2",
      main: "#EF4444",
      dark: "#DC2626",
    },
    info: {
      light: "#E0F2FE",
      main: "#0EA5E9",
      dark: "#0284C7",
    },
    // Landing page colors
    "earth-green": "#6B8E5A",
    "warm-beige": "#F5F1E8",
    "clay-brown": "#8B7355",
    "soft-green": "#9BBF85",
    "tea-brown": "#A0956B",
    cream: "#FDF8F0",
  },

  // 排版系統 - 更新為更易讀、更親和的字體和排版
  typography: {
    fontFamily: {
      // 更新為更圓潔、易讀的字體
      base: "'Nunito', 'Inter', system-ui, Avenir, Helvetica, Arial, sans-serif",
      heading:
        "'Nunito', 'Inter', system-ui, Avenir, Helvetica, Arial, sans-serif",
      mono: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      "sans-tc": ['"Noto Sans TC"', "sans-serif"],
      "serif-tc": ['"Noto Serif TC"', "serif"],
      handwriting: ['"Noto Serif TC"', "serif"],
    },
    // 字體大小 - 增加小標題和大標題之間的差距
    size: {
      xs: "0.75rem", // 12px
      sm: "0.875rem", // 14px
      base: "1rem", // 16px
      lg: "1.125rem", // 18px
      xl: "1.375rem", // 22px - 增加到 22px
      xxl: "1.625rem", // 26px - 增加到 26px
      "2xl": "1.875rem", // 30px - 增加到 30px
      "3xl": "2.25rem", // 36px - 增加到 36px
      "4xl": "2.75rem", // 44px - 增加到 44px
      "5xl": "3.25rem", // 52px - 增加到 52px
      "6xl": "4rem", // 64px - 增加到 64px
    },
    // 字體粗細 - 保持不變
    weight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    // 行高 - 增加行高以提高可讀性
    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375, // 從 1.375 增加到 1.4
      normal: 1.5, // 從 1.5 增加到 1.6
      relaxed: 1.625, // 從 1.625 增加到 1.75
      loose: 2,
      article: 1.8, // 新增適合文章的行高
    },
    // 字間距 - 調整為更易讀的字間距
    letterSpacing: {
      tighter: "-0.03em", // 從 -0.05em 調整為 -0.03em
      tight: "-0.015em", // 從 -0.025em 調整為 -0.015em
      normal: "0",
      wide: "0.015em", // 從 0.025em 調整為 0.015em
      wider: "0.03em", // 從 0.05em 調整為 0.03em
      widest: "0.06em", // 從 0.1em 調整為 0.06em
    },
    // 新增段落間距
    paragraphSpacing: {
      small: "1em",
      medium: "1.5em",
      large: "2em",
    },
  },

  // 過渡效果
  transitions: {
    normal: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
    fast: "100ms cubic-bezier(0.4, 0, 0.2, 1)",
    slow: "300ms cubic-bezier(0.4, 0, 0.2, 1)",
  },

  // 按鈕風格 - 更新為更柔和、更圓潔的按鈕設計
  button: {
    // 主要按鈕 - 更新為新的紅色調
    primary: {
      background: "#FF2E2E", // 更新為新的主色
      hoverBackground: "#E60000", // 更新為新的主色暗色調
      activeBackground: "#B80000", // 更新為新的主色更暗色調
      text: "#FFFFFF",
      padding: "0.875rem 1.75rem", // 增加內邊距
      borderRadius: "0.75rem", // 增加圓角度
      fontSize: "1rem",
      fontWeight: 600,
      shadow:
        "0 8px 15px -3px rgba(255, 46, 46, 0.2), 0 4px 6px -2px rgba(255, 46, 46, 0.1)", // 更新為新的主色陰影
      transition: "all 200ms ease", // 增加過渡時間
    },
    // 次要按鈕 - 更新為新的橘紅色調
    secondary: {
      background: "#FF752E", // 更新為新的次要色
      hoverBackground: "#E65C00", // 更新為新的次要色暗色調
      activeBackground: "#B84700", // 更新為新的次要色更暗色調
      text: "#FFFFFF",
      padding: "0.875rem 1.75rem", // 增加內邊距
      borderRadius: "0.75rem", // 增加圓角度
      fontSize: "1rem",
      fontWeight: 600,
      shadow:
        "0 8px 15px -3px rgba(255, 117, 46, 0.2), 0 4px 6px -2px rgba(255, 117, 46, 0.1)", // 更新為新的次要色陰影
      transition: "all 200ms ease", // 增加過渡時間
    },
    // 輕量按鈕 - 更新為新的主色調
    outline: {
      background: "transparent",
      hoverBackground: "rgba(255, 46, 46, 0.05)", // 更新為新的主色
      activeBackground: "rgba(255, 46, 46, 0.1)", // 更新為新的主色
      text: "#FF2E2E", // 更新為新的主色
      border: "1.5px solid #FF2E2E", // 增加邊框寬度並更新為新的主色
      padding: "0.875rem 1.75rem", // 增加內邊距
      borderRadius: "0.75rem", // 增加圓角度
      fontSize: "1rem",
      fontWeight: 600,
      transition: "all 200ms ease", // 增加過渡時間
    },
    // 文字按鈕 - 更新為新的主色調
    text: {
      background: "transparent",
      hoverBackground: "rgba(255, 46, 46, 0.05)", // 更新為新的主色
      activeBackground: "rgba(255, 46, 46, 0.1)", // 更新為新的主色
      text: "#FF2E2E", // 更新為新的主色
      padding: "0.875rem 1.75rem", // 增加內邊距
      borderRadius: "0.75rem", // 增加圓角度
      fontSize: "1rem",
      fontWeight: 600,
      transition: "all 200ms ease", // 增加過渡時間
    },
    // 尺寸變化 - 增加按鈕尺寸和圓角度
    sizes: {
      small: {
        padding: "0.625rem 1.25rem", // 增加內邊距
        fontSize: "0.875rem",
        borderRadius: "0.625rem", // 增加圓角度
      },
      medium: {
        padding: "0.875rem 1.75rem", // 增加內邊距
        fontSize: "1rem",
        borderRadius: "0.75rem", // 增加圓角度
      },
      large: {
        padding: "1.125rem 2.25rem", // 增加內邊距
        fontSize: "1.125rem",
        borderRadius: "0.875rem", // 增加圓角度
      },
    },
    // 新增圓形按鈕
    icon: {
      small: {
        padding: "0.5rem",
        borderRadius: "9999px",
        width: "2.5rem",
        height: "2.5rem",
      },
      medium: {
        padding: "0.625rem",
        borderRadius: "9999px",
        width: "3rem",
        height: "3rem",
      },
      large: {
        padding: "0.75rem",
        borderRadius: "9999px",
        width: "3.5rem",
        height: "3.5rem",
      },
    },
  },

  // 間距系統 - 調整為更寬鬆的間距，提高易讀性和親和力
  spacing: {
    0: "0",
    px: "1px",
    0.5: "0.125rem", // 2px
    1: "0.25rem", // 4px
    1.5: "0.375rem", // 6px
    2: "0.5rem", // 8px
    2.5: "0.625rem", // 10px
    3: "0.75rem", // 12px
    3.5: "0.875rem", // 14px
    4: "1rem", // 16px
    5: "1.25rem", // 20px
    6: "1.5rem", // 24px
    7: "1.75rem", // 28px - 新增
    8: "2rem", // 32px
    9: "2.25rem", // 36px - 新增
    10: "2.5rem", // 40px
    12: "3rem", // 48px
    14: "3.5rem", // 56px - 新增
    16: "4rem", // 64px
    20: "5rem", // 80px
    24: "6rem", // 96px
    28: "7rem", // 112px - 新增
    32: "8rem", // 128px
    36: "9rem", // 144px - 新增
    40: "10rem", // 160px - 新增
    // 新增語義化間距
    xs: "0.5rem", // 8px
    sm: "1rem", // 16px
    md: "1.5rem", // 24px
    lg: "2rem", // 32px
    xl: "3rem", // 48px
    "2xl": "4rem", // 64px
  },

  // 圓角系統 - 更新為更圓潤的風格，增加親和力
  radius: {
    none: "0",
    xs: "0.125rem",
    sm: "0.375rem",
    md: "0.5rem",
    lg: "0.75rem",
    xl: "1rem",
    "2xl": "1.5rem",
    "3xl": "2rem",
    full: "9999px",
    // 新增語義化圓角
    button: "0.75rem", // 按鈕圓角
    card: "1.25rem", // 卡片圓角
    dialog: "1.5rem", // 對話框圓角
    pill: "9999px", // 膠囊形狀
  },

  // 陰影系統 - 更新為更柔和的陰影風格，減少銳利感
  shadows: {
    xs: "0 1px 2px 0 rgba(0, 0, 0, 0.02)", // 降低不透明度
    sm: "0 1px 3px 0 rgba(0, 0, 0, 0.03), 0 1px 2px 0 rgba(0, 0, 0, 0.02)", // 降低不透明度
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.06), 0 2px 4px -1px rgba(0, 0, 0, 0.03)", // 降低不透明度
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03)", // 降低不透明度
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 10px 10px -5px rgba(0, 0, 0, 0.02)", // 降低不透明度
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.1)", // 從 0.15 降低到 0.1
    inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.02)", // 從 0.04 降低到 0.02
    // 更新為新的主色調陰影
    colored:
      "0 10px 25px -5px rgba(255, 46, 46, 0.2), 0 5px 15px -5px rgba(255, 46, 46, 0.1)", // 主色陰影，降低不透明度並更新顏色
    "colored-lg":
      "0 20px 35px -10px rgba(255, 46, 46, 0.2), 0 10px 20px -5px rgba(255, 46, 46, 0.1)", // 大型主色陰影，降低不透明度並更新顏色
    // 更新為新的次要色調陰影
    blue: "0 10px 25px -5px rgba(14, 165, 233, 0.2), 0 5px 15px -5px rgba(14, 165, 233, 0.1)", // 藍色陰影，降低不透明度並更新顏色
    // 新增語義化陰影
    subtle: "0 2px 8px -2px rgba(0, 0, 0, 0.05)", // 輕微的陰影效果
    floating:
      "0 8px 16px -4px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.03)", // 漂浮效果
    elevated:
      "0 16px 24px -6px rgba(0, 0, 0, 0.06), 0 6px 12px -3px rgba(0, 0, 0, 0.02)", // 提升效果
    none: "none",
  },

  // 卡片風格 - 更新為更圓潤、更柔和的卡片設計
  card: {
    default: {
      borderRadius: "1.25rem", // 從 16px 增加到 20px
      padding: "1.75rem", // 從 24px 增加到 28px
      shadow:
        "0 8px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03)", // 柔化陰影
      border: "1px solid rgba(0, 0, 0, 0.03)", // 新增細微邊框增強可視性
      transition: "all 200ms ease", // 增加過渡效果
    },
    compact: {
      borderRadius: "1rem", // 從 12px 增加到 16px
      padding: "1.25rem", // 從 16px 增加到 20px
      shadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.04), 0 2px 4px -1px rgba(0, 0, 0, 0.02)", // 柔化陰影
      border: "1px solid rgba(0, 0, 0, 0.02)", // 新增細微邊框增強可視性
      transition: "all 200ms ease", // 增加過渡效果
    },
    elevated: {
      borderRadius: "1.5rem", // 從 20px 增加到 24px
      padding: "2.25rem", // 從 32px 增加到 36px
      shadow:
        "0 16px 24px -6px rgba(0, 0, 0, 0.06), 0 6px 12px -3px rgba(0, 0, 0, 0.02)", // 柔化陰影
      border: "1px solid rgba(0, 0, 0, 0.01)", // 新增細微邊框增強可視性
      transition: "all 200ms ease", // 增加過渡效果
    },
    // 新增互動式卡片
    interactive: {
      borderRadius: "1.25rem", // 20px
      padding: "1.75rem", // 28px
      shadow:
        "0 8px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.03)", // 柔化陰影
      border: "1px solid rgba(0, 0, 0, 0.03)", // 新增細微邊框增強可視性
      transition: "all 200ms ease", // 增加過渡效果
      hoverShadow:
        "0 16px 24px -6px rgba(0, 0, 0, 0.06), 0 6px 12px -3px rgba(0, 0, 0, 0.02)", // 懸停時的陰影
      hoverTransform: "translateY(-4px)", // 懸停時的位移效果
    },
    // 新增柔和卡片
    soft: {
      borderRadius: "1.25rem", // 20px
      padding: "1.75rem", // 28px
      shadow: "0 4px 8px -2px rgba(0, 0, 0, 0.03)", // 非常柔和的陰影
      border: "1px solid rgba(0, 0, 0, 0.02)", // 細微邊框
      background: "rgba(255, 255, 255, 0.7)", // 半透明背景
      backdropFilter: "blur(8px)", // 背景模糊效果
      transition: "all 200ms ease", // 增加過渡效果
    },
  },

  // 過渡效果
  transition: {
    DEFAULT: "150ms cubic-bezier(0.4, 0, 0.2, 1)",
    normal: "150ms cubic-bezier(0.4, 0, 0.2, 1)", // 從 DEFAULT 複製來的正常過渡效果
    fast: "100ms cubic-bezier(0.4, 0, 0.2, 1)",
    slow: "300ms cubic-bezier(0.4, 0, 0.2, 1)",
  },

  // z-index 系統
  zIndex: {
    0: 0,
    10: 10, // 基礎元素
    20: 20, // 導航
    30: 30, // 固定元素
    40: 40, // 抽屜
    50: 50, // 模態框
    auto: "auto",
  },
};

export default tokens;
